import React, { useState } from "react";
import { Star } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * StarRating — display + interactive 1..5 star rating component.
 *
 * Props:
 *   value:     number   current rating (0..5)
 *   onChange:  fn       called with new rating when star is clicked (interactive)
 *   readOnly:  boolean  if true, no hover/click interaction
 *   size:      "sm"|"md"|"lg"  star size
 *   testid:    string   data-testid prefix
 */
export const StarRating = ({
  value = 0,
  onChange,
  readOnly = false,
  size = "md",
  testid = "star-rating",
}) => {
  const [hover, setHover] = useState(0);
  const sizes = {
    sm: "h-4 w-4",
    md: "h-5 w-5",
    lg: "h-7 w-7",
  };
  const sizeClass = sizes[size] || sizes.md;

  const display = readOnly ? value : (hover || value);

  return (
    <div
      data-testid={testid}
      className="inline-flex items-center gap-1"
      role={readOnly ? "img" : "radiogroup"}
      aria-label={readOnly ? `${value} of 5 stars` : "Rate from 1 to 5"}
      onMouseLeave={() => !readOnly && setHover(0)}
    >
      {[1, 2, 3, 4, 5].map((n) => {
        const filled = n <= display;
        return (
          <button
            key={n}
            type="button"
            disabled={readOnly}
            data-testid={`${testid}-star-${n}`}
            aria-label={`${n} star${n > 1 ? "s" : ""}`}
            onMouseEnter={() => !readOnly && setHover(n)}
            onClick={() => !readOnly && onChange?.(n)}
            className={cn(
              "p-0.5 transition-transform",
              !readOnly && "hover:scale-110 cursor-pointer",
              readOnly && "cursor-default"
            )}
          >
            <Star
              className={cn(
                sizeClass,
                "transition-colors",
                filled ? "fill-[#D4A437] text-[#D4A437]" : "fill-transparent text-foreground/30"
              )}
              strokeWidth={1.5}
            />
          </button>
        );
      })}
    </div>
  );
};

export default StarRating;
